/**
 * TestFitStudio — the merged CAD/GIS workspace for "Place the solution and
 * verify containment": one canvas (map + blueprint projections), a ribbon of
 * tools, a contents tree, a properties inspector and a status bar with yield
 * + takeoffs. Successor of the separate Plan Sheet and Map Preview tabs.
 *
 * Replay discipline: the studio renders solver output and writes ONLY
 * through patchLayout — pins, customs, barriers, frontline and wind land as
 * frozen fields on the scenario input (schemaVersion 2 for studio geometry),
 * which the server re-solves and replays at publish.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MousePointer2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import type {
  BuildGenerationParcelTestFitInput,
  GenerationParcelTestFitResult,
} from '../../../../lib/generationImpact/generationLayoutGeometry';
import {
  crosswindRowRotationDeg,
  frontlineRotationDeg,
} from '../../../../lib/generationImpact/generationLayoutFrontline';
import {
  BARRIER_MAX_HEIGHT_M,
  BARRIER_MIN_HEIGHT_M,
} from '../../../../lib/generationImpact/noiseField';
import { hashGenerationImpactValue } from '../../../../lib/generationImpact/integrity';
import { fetchWindRose } from '../../../../services/dataProviders/windRose';
import type {
  GenerationAcousticBarrier,
  GenerationCustomComponentKind,
  GenerationLayoutConfiguration,
  GenerationLayoutWindRose,
  GenerationProjectInstalledReservations,
  NoiseFieldResult,
  GenerationMaxPowerStudySnapshot,
} from '../../../../types/generationImpact';
import {
  addCustomComponent,
  componentDragPatch,
  connectWithRoadsPatch,
  packSelectionPatch,
  pinAllComponents,
  unpinAllComponents,
} from './studioLayoutEdits';
import { unassignImportedSource } from './studioImportAssignments';
import {
  bundleFromTestFit,
  type LayoutExportBundle,
} from '../../../../services/geoExport/layoutExportBundle';
import {
  downloadBundleAsDwg,
  downloadBundleAsDxf,
  downloadBundleAsKmz,
  downloadBundleAsShapefile,
  DwgExportUnavailableError,
  type StudioExportFormat,
} from '../../../../services/geoExport/exportBundleDownloads';
import type { SiteBoundaryParcel } from '../../../../types/siteBoundary';
import type { SiteEntity, SiteLayer, SiteLayerCategory } from '../../../../types/site';
import { StudioCanvas, type StudioCanvasHandle } from './StudioCanvas';
import { StudioExportDialog } from './StudioExportDialog';
import { StudioImportDialog } from './StudioImportDialog';
import {
  StudioContentsPane,
  type StudioContentsTarget,
} from './StudioContentsPane';
import { StudioInspector } from './StudioInspector';
import { StudioMultiSelectTools } from './StudioMultiSelectTools';
import { StudioLayoutBar } from './StudioLayoutBar';
import { StudioRibbon } from './StudioRibbon';
import { StudioStatusBar, type StudioSaveState } from './StudioStatusBar';
import {
  StudioVerifyRail,
  type StudioVerifyIssueNavigation,
} from './StudioVerifyRail';
import { useStudioStore } from './studioStore';
import {
  EMPTY_STUDIO_LAYOUT_HISTORY,
  recordStudioLayoutChange,
  redoStudioLayoutChange,
  undoStudioLayoutChange,
  type StudioLayoutHistoryState,
} from './studioLayoutHistory';
import {
  readStudioLayerState,
  writeStudioLayerState,
} from './studioLayerMemory';
import { layerCategoryKey } from './studioLayers';
import { useStore } from '../../../../store';
import {
  duplicateSiteLayers,
  mergeSiteLayers,
  patchSiteLayer,
  removeSiteLayer,
  removeSiteLayers,
} from '../../../../lib/siteLayerEditor';

const BARRIER_COLOR = '#22d3ee';
const FRONTLINE_COLOR = '#F59E0B';
const ROAD_DRAW_COLOR = '#94A3B8';
const CUSTOM_DRAW_COLOR = '#E11D48';
/** Default corridor width (m) for Assist › Connect with roads centerlines. */
const CONNECTOR_ROAD_WIDTH_M = 8;

type BarrierDrawKind = GenerationAcousticBarrier['kind'];

interface PendingBarrier {
  kind: BarrierDrawKind;
  path: Array<{ lat: number; lng: number }>;
}

/** A sketched road centerline awaiting its required corridor width. */
interface PendingRoad {
  path: Array<{ lat: number; lng: number }>;
}

function usePhoneReviewMode(): boolean {
  const [isPhone, setIsPhone] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(max-width: 599px)');
    const update = (): void => setIsPhone(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return isPhone;
}

function useCompactStudioSheets(): boolean {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(max-width: 959px)');
    const update = (): void => setCompact(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  return compact;
}

/** Click-to-place footprint defaults (immediately editable in the inspector). */
const PLACE_DEFAULTS: Partial<Record<string, {
  kind: GenerationCustomComponentKind;
  label: string;
  widthM: number;
  heightM: number;
}>> = {
  'place-parking': { kind: 'parking', label: 'Parking', widthM: 60, heightM: 30 },
  'place-building': { kind: 'building', label: 'Building', widthM: 40, heightM: 25 },
  'place-data-center': { kind: 'data-center', label: 'Data center', widthM: 100, heightM: 60 },
  'place-generator': { kind: 'generator', label: 'Generator', widthM: 15, heightM: 10 },
};

/** Compact 16-sector wind rose; petals point toward the FROM direction. */
function WindRoseBadge({ windRose }: { windRose: GenerationLayoutWindRose }): React.JSX.Element {
  const size = 84;
  const centre = size / 2;
  const maxRadius = centre - 8;
  const maxFrequency = Math.max(1, ...windRose.sectors.map(sector => sector.frequencyPct));
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`Wind rose; prevailing wind from ${windRose.prevailingFromDeg} degrees`}
      data-testid="generation-wind-rose"
    >
      <circle cx={centre} cy={centre} r={maxRadius} fill="rgba(15,23,42,0.55)" stroke="#94a3b8" strokeWidth="1" />
      {windRose.sectors.map(sector => {
        const radius = maxRadius * (sector.frequencyPct / maxFrequency);
        if (radius <= 1) return null;
        const angle = sector.fromDeg * Math.PI / 180;
        const half = Math.PI / 16 * 0.8;
        const tipX = centre + radius * Math.sin(angle);
        const tipY = centre - radius * Math.cos(angle);
        const leftX = centre + radius * 0.35 * Math.sin(angle - half);
        const leftY = centre - radius * 0.35 * Math.cos(angle - half);
        const rightX = centre + radius * 0.35 * Math.sin(angle + half);
        const rightY = centre - radius * 0.35 * Math.cos(angle + half);
        const prevailing = sector.fromDeg === windRose.prevailingFromDeg;
        return (
          <path
            key={sector.fromDeg}
            d={`M ${centre} ${centre} L ${leftX} ${leftY} L ${tipX} ${tipY} L ${rightX} ${rightY} Z`}
            fill={prevailing ? '#f59e0b' : '#38bdf8'}
            fillOpacity={prevailing ? 0.95 : 0.7}
          />
        );
      })}
      <text x={centre} y={9} textAnchor="middle" fill="#e2e8f0" fontSize="8" fontWeight="700">N</text>
    </svg>
  );
}

export interface TestFitStudioProps {
  scenarioKey: string;
  /** Raw scenario identity for exported engineering metadata. */
  scenarioId?: string;
  /** Saved updatedAt/hash label, or "Unsaved draft". */
  scenarioRevision?: string;
  site: SiteEntity;
  testFit: GenerationParcelTestFitResult;
  parcels: readonly SiteBoundaryParcel[];
  activeLayout: GenerationLayoutConfiguration;
  readOnly: boolean;
  /** Blocks geometry mutations while the workbench is saving/publishing. */
  busy?: boolean;
  /** Full editor, bounded embedded preview, or the rollout compatibility shell. */
  displayMode?: 'editor' | 'preview' | 'legacy';
  onCommitLayout: (
    label: string,
    patch: Partial<GenerationLayoutConfiguration>,
  ) => void;
  /** Replaces the complete layout for undo/redo, including removed fields. */
  onReplaceLayout: (layout: GenerationLayoutConfiguration) => void;
  /** Grow the physical generation fleet by one (sanctioned fleet transaction). */
  onAddGenerationBlock?: () => void;
  /** Scenario save telemetry shown in the Studio status line. */
  saveState?: StudioSaveState;
  /** Optional save command supplied by the full-screen workbench host. */
  onSave?: () => void;
  /** Embedded-preview action that opens the precision editor. */
  onOpenStudio?: () => void;
  onSelectParcel: (parcelId: string) => void;
  layoutSearchBase: Omit<BuildGenerationParcelTestFitInput, 'layout'>;
  installedReservations?: GenerationProjectInstalledReservations;
  noiseField?: NoiseFieldResult | null;
  noiseVisible: boolean;
  onToggleNoise: () => void;
  acousticBarriers: readonly GenerationAcousticBarrier[];
  /**
   * Max-power envelope for the current parcel + layout. The Studio COMPUTES
   * NOTHING here — the workbench owns the sweep (it holds the scenario input);
   * the Studio owns the APPLY so the placement change lands on the normal
   * layout-history path and stays undoable.
   */
  maxPower?: GenerationMaxPowerStudySnapshot | null;
  maxPowerBusy?: boolean;
  onRunMaxPower?: () => void;
}

export const TestFitStudio: React.FC<TestFitStudioProps> = ({
  scenarioKey,
  scenarioId,
  scenarioRevision,
  site,
  testFit,
  parcels,
  activeLayout,
  readOnly,
  busy = false,
  displayMode = 'editor',
  onCommitLayout,
  onReplaceLayout,
  onAddGenerationBlock,
  saveState,
  onSave,
  onOpenStudio,
  onSelectParcel,
  layoutSearchBase,
  installedReservations,
  noiseField,
  noiseVisible,
  onToggleNoise,
  acousticBarriers,
  maxPower,
  maxPowerBusy,
  onRunMaxPower,
}) => {
  const {
    tool, projection, ribbonTab, leftPane, leftDockOpen, rightPane, selectedId, selectedIds,
    contentsOpen, layoutPanelOpen, inspectorOpen,
    takeoffsOpen, fitOpen,
    setTool, cancelActiveTool, setProjection, setRibbonTab, setLeftPane, toggleLeftDock,
    setRightPane, select, toggleSelect, toggleContents, toggleLayoutPanel, toggleInspector, toggleTakeoffs,
    toggleFit, resetForScenario,
  } = useStudioStore();
  useEffect(() => resetForScenario(scenarioKey), [resetForScenario, scenarioKey]);
  const canvasRef = useRef<StudioCanvasHandle>(null);
  const phoneReviewMode = usePhoneReviewMode();
  const compactStudioSheets = useCompactStudioSheets();
  const mutationBlocked = readOnly || busy || displayMode === 'preview' || phoneReviewMode;
  const [layoutHistory, setLayoutHistory] = useState<StudioLayoutHistoryState>(
    EMPTY_STUDIO_LAYOUT_HISTORY,
  );
  const [issueTraceId, setIssueTraceId] = useState<string | null>(null);
  const issueTraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setLayoutHistory(EMPTY_STUDIO_LAYOUT_HISTORY);
    setIssueTraceId(null);
    if (issueTraceTimerRef.current) clearTimeout(issueTraceTimerRef.current);
  }, [scenarioKey]);
  useEffect(() => () => {
    if (issueTraceTimerRef.current) clearTimeout(issueTraceTimerRef.current);
  }, []);
  const previousTargetParcelRef = useRef(activeLayout.targetParcelId);
  useEffect(() => {
    if (previousTargetParcelRef.current === activeLayout.targetParcelId) return;
    previousTargetParcelRef.current = activeLayout.targetParcelId;
    if (typeof requestAnimationFrame !== 'function') return;
    const frame = requestAnimationFrame(() => canvasRef.current?.fit('target-parcel'));
    return () => cancelAnimationFrame(frame);
  }, [activeLayout.targetParcelId]);

  const commitLayoutChange = useCallback((
    label: string,
    patch: Partial<GenerationLayoutConfiguration>,
  ): boolean => {
    if (mutationBlocked) return false;
    const after: GenerationLayoutConfiguration = {
      ...activeLayout,
      ...patch,
      schemaVersion: 2,
    };
    setLayoutHistory((current) => recordStudioLayoutChange(
      current,
      label,
      activeLayout,
      after,
    ));
    onCommitLayout(label, { ...patch, schemaVersion: 2 });
    return true;
  }, [activeLayout, mutationBlocked, onCommitLayout]);

  /**
   * Apply the placement the max-power study found. Routed through
   * `commitLayoutChange` so it records a history entry and is undoable like any
   * other Studio edit — a capacity study should never mutate geometry by a path
   * the user cannot reverse.
   */
  const applyMaxPowerPlacement = useCallback((values: Record<string, number>): void => {
    if (mutationBlocked) return;
    const patch: Partial<GenerationLayoutConfiguration> = {};
    if (typeof values.rotationDeg === 'number') patch.rotationDeg = values.rotationDeg;
    if (typeof values.generationBlockAspectRatio === 'number') {
      patch.generationBlockAspectRatio = values.generationBlockAspectRatio;
    }
    if (Object.keys(patch).length === 0) return;
    commitLayoutChange('Apply max-power placement', patch);
  }, [commitLayoutChange, mutationBlocked]);

  const replaceLayout = useCallback((layout: GenerationLayoutConfiguration): void => {
    if (mutationBlocked) return;
    onReplaceLayout(structuredClone(layout));
  }, [mutationBlocked, onReplaceLayout]);

  const undoLayout = useCallback(() => {
    const step = undoStudioLayoutChange(layoutHistory);
    if (!step || mutationBlocked) return;
    setLayoutHistory(step.state);
    replaceLayout(step.layout);
    toast.success(`Undid: ${step.label}`);
  }, [layoutHistory, mutationBlocked, replaceLayout]);

  const redoLayout = useCallback(() => {
    const step = redoStudioLayoutChange(layoutHistory);
    if (!step || mutationBlocked) return;
    setLayoutHistory(step.state);
    replaceLayout(step.layout);
    toast.success(`Redid: ${step.label}`);
  }, [layoutHistory, mutationBlocked, replaceLayout]);

  // Assist › Create starting layout: drop manual pins and reset the auto-pack
  // orientation/scheme so the solver produces a clean packed layout. Custom
  // objects, exclusions and imported geometry are untouched.
  // Layers › Imported Sources › Unassign: remove the source's derived geometry
  // and record. Equipment returns to the packer; the selection is cleared since
  // the artifact it may have pointed at is gone.
  const handleUnassignSource = useCallback((assignmentId: string) => {
    const patch = unassignImportedSource(activeLayout, assignmentId);
    if (patch && commitLayoutChange('Unassign imported source', patch)) {
      select(null);
      toast.success('Unassigned imported source');
    }
  }, [activeLayout, commitLayoutChange, select]);

  const handleCreateStartingLayout = useCallback(() => {
    if (commitLayoutChange('Create starting layout', {
      manualPlacements: [],
      orientation: 'auto',
      packingScheme: 'auto',
    })) {
      select(null);
      toast.success('Created a fresh auto-packed layout');
    }
  }, [commitLayoutChange, select]);

  useEffect(() => {
    if (!mutationBlocked) return;
    cancelActiveTool();
  }, [cancelActiveTool, mutationBlocked]);
  useEffect(() => {
    if (!compactStudioSheets) return;
    const frame = requestAnimationFrame(() => {
      const state = useStudioStore.getState();
      if (state.leftDockOpen) state.toggleLeftDock();
      if (state.inspectorOpen) state.toggleInspector();
    });
    return () => cancelAnimationFrame(frame);
  }, [
    compactStudioSheets,
    scenarioKey,
  ]);
  useEffect(() => {
    if (!compactStudioSheets || !leftDockOpen || !inspectorOpen) return;
    toggleInspector();
  }, [compactStudioSheets, inspectorOpen, leftDockOpen, toggleInspector]);
  const toggleLeftDockForViewport = useCallback(() => {
    if (compactStudioSheets && !leftDockOpen && inspectorOpen) toggleInspector();
    toggleLeftDock();
  }, [
    compactStudioSheets,
    inspectorOpen,
    leftDockOpen,
    toggleInspector,
    toggleLeftDock,
  ]);
  const toggleInspectorForViewport = useCallback(() => {
    if (compactStudioSheets && !inspectorOpen && leftDockOpen) toggleLeftDock();
    toggleInspector();
  }, [
    compactStudioSheets,
    inspectorOpen,
    leftDockOpen,
    toggleInspector,
    toggleLeftDock,
  ]);

  const [fetchingWind, setFetchingWind] = useState(false);
  const windRequestRef = useRef(0);
  const windAbortRef = useRef<AbortController | null>(null);
  const windScope = `${site.id}:${scenarioKey}:${testFit.anchor.lat.toFixed(7)}:${testFit.anchor.lng.toFixed(7)}`;
  const windScopeRef = useRef(windScope);
  windScopeRef.current = windScope;
  useEffect(() => {
    windRequestRef.current += 1;
    windAbortRef.current?.abort();
    windAbortRef.current = null;
    setFetchingWind(false);
    return () => windAbortRef.current?.abort();
  }, [windScope]);
  // Barrier sketching: pick a kind → click vertices → enter the REQUIRED
  // height (never defaulted) → the barrier lands in layout.acousticBarriers.
  const [pendingBarrier, setPendingBarrier] = useState<PendingBarrier | null>(null);
  const [pendingHeight, setPendingHeight] = useState('');
  const [pendingLabel, setPendingLabel] = useState('');
  // Road sketching: draw the centerline → enter the REQUIRED corridor width.
  const [pendingRoad, setPendingRoad] = useState<PendingRoad | null>(null);
  const [pendingRoadWidth, setPendingRoadWidth] = useState('');
  const [pendingRoadLabel, setPendingRoadLabel] = useState('');
  // Uncommitted sketches belong to the scenario in which they were drawn.
  // Never offer an old path for saving after the host selects another one.
  useEffect(() => {
    setPendingBarrier(null);
    setPendingHeight('');
    setPendingLabel('');
    setPendingRoad(null);
    setPendingRoadWidth('');
    setPendingRoadLabel('');
  }, [scenarioKey, site.id]);
  // Import-to-studio: ribbon button or files dropped onto the canvas.
  const [importOpen, setImportOpen] = useState(false);
  const [importFiles, setImportFiles] = useState<File[] | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<StudioExportFormat | null>(null);
  const [exportBundle, setExportBundle] = useState<LayoutExportBundle | null>(null);
  const importInputFingerprint = useMemo(() => hashGenerationImpactValue({
    site: { id: site.id, location: site.location ?? null },
    scenarioKey,
    layout: activeLayout,
    targetParcel: testFit.parcel ?? null,
    reservations: installedReservations ?? null,
    search: layoutSearchBase,
  }), [
    activeLayout,
    installedReservations,
    layoutSearchBase,
    scenarioKey,
    site.id,
    site.location,
    testFit.parcel,
  ]);
  const importSessionKey = `${site.id}:${scenarioKey}:${importInputFingerprint}`;
  const currentImportSessionRef = useRef(importSessionKey);
  currentImportSessionRef.current = importSessionKey;
  const mutationBlockedRef = useRef(mutationBlocked);
  mutationBlockedRef.current = mutationBlocked;
  useEffect(() => {
    setImportOpen(false);
    setImportFiles(null);
  }, [importSessionKey]);
  const anchor = testFit.anchor;
  const customCount = activeLayout.customComponents?.length ?? 0;

  // Plain click selects; Shift/Cmd/Ctrl-click extends the multi-selection.
  const handleSelect = useCallback((id: string | null, additive?: boolean) => {
    if (additive && id != null) toggleSelect(id);
    else select(id);
  }, [select, toggleSelect]);
  // Resolved polygons for the current multi-selection — the input to the
  // contextual align/distribute/rotate/nudge tools.
  const selectedComponents = useMemo(
    () => testFit.components.filter(component => selectedIds.includes(component.rectangle.id)),
    [testFit.components, selectedIds],
  );
  // Placement readout: pinned (manual, dashed) vs auto-packed components.
  // Placement counts describe the PACKER'S FLEET, and must agree with what the
  // bulk pin/unpin actions actually operate on (`pinAllComponents`): access
  // roads are re-cut derived geometry and customs carry their own position, so
  // neither is packer-placed. Anything not explicitly pinned is auto — the
  // solver only ever stamps `placement: 'pinned'`, never `'packed'`, so testing
  // for 'packed' here counted nothing and the readout could never show a split.
  const placementCounts = useMemo(() => {
    const customIds = new Set((activeLayout.customComponents ?? []).map(custom => custom.id));
    let pinned = 0;
    let auto = 0;
    for (const component of testFit.components) {
      const rectangle = component.rectangle;
      if (rectangle.kind === 'access-road' || customIds.has(rectangle.id)) continue;
      if (rectangle.placement === 'pinned') pinned += 1;
      else auto += 1;
    }
    return { pinned, auto };
  }, [testFit.components, activeLayout.customComponents]);
  const siteLayers = site.siteLayers ?? [];
  const [layerVisibility, setLayerVisibility] = useState<Record<string, boolean>>({});
  const [siteLayerOpacity, setSiteLayerOpacity] = useState<Record<string, number>>({});
  const [hiddenComponentIds, setHiddenComponentIds] = useState<Record<string, boolean>>({});
  const [lockedObjectIds, setLockedObjectIds] = useState<Record<string, boolean>>({});
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(() => new Set());
  // Wind rose is a view overlay like noise. It reveals itself once when wind
  // data first arrives (so fetching visibly does something), then the user owns it.
  const [windVisible, setWindVisible] = useState(false);
  const hadWindRef = useRef(false);
  useEffect(() => {
    const has = activeLayout.windRose != null;
    if (has && !hadWindRef.current) setWindVisible(true);
    hadWindRef.current = has;
  }, [activeLayout.windRose]);
  // Layer view-state (visibility/opacity/lock) is an OPERATOR PREFERENCE that
  // persists across reloads per scenario — it NEVER enters the frozen
  // schemaVersion:2 layout input or the replay hash (studioLayerMemory keeps it
  // in a separate localStorage namespace, exactly like the camera memory).
  // `hydratedKeyRef` tracks which scenario the maps currently reflect;
  // `skipPersistRef` swallows the persist that fires in the same commit as a
  // hydrate, so switching scenarios never writes the previous scenario's maps
  // under the new key.
  const hydratedKeyRef = useRef<string | null>(null);
  const skipPersistRef = useRef(false);
  useEffect(() => {
    const saved = readStudioLayerState(scenarioKey);
    const siteLayerIds = new Set((site.siteLayers ?? []).map(layer => layer.id));
    const componentIds = new Set(testFit.components.map(component => component.rectangle.id));
    const nextVisibility: Record<string, boolean> = {};
    const nextHiddenComponents: Record<string, boolean> = {};
    for (const id of saved?.hidden ?? []) {
      // A hidden site-layer forces visible:false; a hidden component id maps to
      // the component set. Ids matching neither (a layer/component that no
      // longer exists — e.g. a re-imported layer with a fresh id) are DROPPED
      // so the persisted set self-heals instead of accumulating phantoms.
      if (siteLayerIds.has(id)) nextVisibility[id] = false;
      else if (componentIds.has(id)) nextHiddenComponents[id] = true;
    }
    const nextLocked: Record<string, boolean> = {};
    for (const id of saved?.locked ?? []) {
      if (siteLayerIds.has(id) || componentIds.has(id)) nextLocked[id] = true;
    }
    setLayerVisibility(nextVisibility);
    setSiteLayerOpacity(saved?.opacity ?? {});
    setHiddenComponentIds(nextHiddenComponents);
    setLockedObjectIds(nextLocked);
    setHiddenCategories(new Set(saved?.hiddenCategories ?? []));
    hydratedKeyRef.current = scenarioKey;
    skipPersistRef.current = true;
    // Re-hydrate only on a scenario/site switch — not when site.siteLayers
    // mutates — so operator toggles are never wiped mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioKey, site.id]);
  useEffect(() => {
    if (hydratedKeyRef.current !== scenarioKey) return;
    if (skipPersistRef.current) {
      skipPersistRef.current = false;
      return;
    }
    const siteLayerIds = new Set((site.siteLayers ?? []).map(layer => layer.id));
    const componentIds = new Set(testFit.components.map(component => component.rectangle.id));
    const knownId = (id: string): boolean => siteLayerIds.has(id) || componentIds.has(id);
    const hidden: string[] = [];
    for (const [id, visible] of Object.entries(layerVisibility)) {
      if (visible === false && siteLayerIds.has(id)) hidden.push(id);
    }
    for (const [id, isHidden] of Object.entries(hiddenComponentIds)) {
      if (isHidden && componentIds.has(id)) hidden.push(id);
    }
    const locked = Object.entries(lockedObjectIds)
      .filter(([id, isLocked]) => isLocked && knownId(id))
      .map(([id]) => id);
    const opacity = Object.keys(siteLayerOpacity).length > 0 ? siteLayerOpacity : undefined;
    // Persist only category keys that still have a layer, so deleting/renaming a
    // category self-heals the saved set instead of accumulating phantoms.
    const liveCategoryKeys = new Set((site.siteLayers ?? []).map(layerCategoryKey));
    const hiddenCategoryList = [...hiddenCategories].filter(key => liveCategoryKeys.has(key));
    writeStudioLayerState(scenarioKey, {
      hidden,
      locked,
      ...(opacity ? { opacity } : {}),
      ...(hiddenCategoryList.length > 0 ? { hiddenCategories: hiddenCategoryList } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioKey, layerVisibility, siteLayerOpacity, hiddenComponentIds, lockedObjectIds, hiddenCategories]);
  const toggleCategory = useCallback((categoryKey: string): void => {
    setHiddenCategories(current => {
      const next = new Set(current);
      if (next.has(categoryKey)) next.delete(categoryKey);
      else next.add(categoryKey);
      return next;
    });
  }, []);
  const studioSiteLayers = useMemo(() => siteLayers.map(layer => {
    // A hidden CATEGORY forces every member layer hidden on the canvas,
    // overriding the per-layer visibility until the category is shown again.
    const categoryHidden = hiddenCategories.has(layerCategoryKey(layer));
    if (categoryHidden) return { ...layer, visible: false };
    return layerVisibility[layer.id] == null
      ? layer
      : { ...layer, visible: layerVisibility[layer.id] };
  }), [layerVisibility, siteLayers, hiddenCategories]);
  // Site-reference layer edits are SITE TRUTH — they mutate `site.siteLayers`
  // through the canonical `updateSiteLayers` writer (optimistic local update +
  // Firestore persist), NOT the frozen scenario input. The updater form runs
  // against the store's current layers so it never races the studio's
  // visibility-overridden `studioSiteLayers` view. `select(null)` after a delete
  // clears the now-dangling selection.
  const updateSiteLayers = useStore(state => state.updateSiteLayers);
  const patchSiteLayerField = useCallback((
    id: string,
    patch: Partial<Pick<(typeof siteLayers)[number], 'name' | 'color' | 'category' | 'customCategory'>>,
  ) => {
    if (mutationBlocked || lockedObjectIds[id]) return;
    void updateSiteLayers(site.id, current => patchSiteLayer([...current], id, patch));
  }, [lockedObjectIds, mutationBlocked, site.id, updateSiteLayers]);
  const deleteSiteLayerById = useCallback((id: string) => {
    if (mutationBlocked || lockedObjectIds[id]) return;
    void updateSiteLayers(site.id, current => removeSiteLayer([...current], id));
    select(null);
  }, [lockedObjectIds, mutationBlocked, site.id, updateSiteLayers, select]);
  // Bulk site-layer operations over the multi-selection (all SITE TRUTH via
  // updateSiteLayers, except Hide which is view-state only).
  const copySiteLayers = useCallback((ids: readonly string[]) => {
    if (mutationBlocked || ids.some(id => lockedObjectIds[id])) return;
    void updateSiteLayers(site.id, current => duplicateSiteLayers([...current], ids));
  }, [lockedObjectIds, mutationBlocked, site.id, updateSiteLayers]);
  const deleteSiteLayers = useCallback((ids: readonly string[]) => {
    if (mutationBlocked || ids.some(id => lockedObjectIds[id])) return;
    void updateSiteLayers(site.id, current => removeSiteLayers([...current], ids));
    select(null);
  }, [lockedObjectIds, mutationBlocked, site.id, updateSiteLayers, select]);
  const mergeSiteLayersAction = useCallback((ids: readonly string[]) => {
    if (mutationBlocked || ids.some(id => lockedObjectIds[id])) return;
    void updateSiteLayers(site.id, current => mergeSiteLayers([...current], ids));
    select(null);
  }, [lockedObjectIds, mutationBlocked, site.id, updateSiteLayers, select]);
  const hideSiteLayers = useCallback((ids: readonly string[]) => {
    setLayerVisibility((current) => {
      const next = { ...current };
      for (const id of ids) next[id] = false;
      return next;
    });
  }, []);
  const moveSiteLayers = useCallback((ids: readonly string[], category: SiteLayerCategory) => {
    if (mutationBlocked || ids.some(id => lockedObjectIds[id])) return;
    void updateSiteLayers(site.id, current =>
      ids.reduce<SiteLayer[]>((acc, id) => patchSiteLayer(acc, id, { category }), [...current]));
  }, [lockedObjectIds, mutationBlocked, site.id, updateSiteLayers]);
  // Cmd/Ctrl+S saves the scenario (the single Save affordance lives in the
  // studio title bar; this keyboard shortcut routes to the same handler instead
  // of a duplicate in-ribbon button). Preventing the default stops the browser's
  // own save dialog.
  useEffect(() => {
    if (displayMode !== 'editor' || !onSave) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 's' || event.key === 'S')) {
        event.preventDefault();
        if (!mutationBlocked && !busy) onSave();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [displayMode, onSave, mutationBlocked, busy]);
  const hiddenComponentIdSet = useMemo(
    () => new Set(
      Object.entries(hiddenComponentIds)
        .filter(([, hidden]) => hidden)
        .map(([id]) => id),
    ),
    [hiddenComponentIds],
  );
  const lockedObjectIdSet = useMemo(
    () => new Set(
      Object.entries(lockedObjectIds)
        .filter(([, locked]) => locked)
        .map(([id]) => id),
    ),
    [lockedObjectIds],
  );
  const selectionLocked = selectedIds.some(id => lockedObjectIdSet.has(id));
  const [leftDockWidth, setLeftDockWidth] = useState(296);
  const [rightDockWidth, setRightDockWidth] = useState(328);

  const beginDockResize = useCallback((
    side: 'left' | 'right',
    event: React.PointerEvent<HTMLButtonElement>,
  ): void => {
    if (displayMode === 'preview') return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = side === 'left' ? leftDockWidth : rightDockWidth;
    const onMove = (moveEvent: PointerEvent): void => {
      const delta = moveEvent.clientX - startX;
      const next = side === 'left' ? startWidth + delta : startWidth - delta;
      const clamped = Math.max(240, Math.min(420, next));
      if (side === 'left') setLeftDockWidth(clamped);
      else setRightDockWidth(clamped);
    };
    const onEnd = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd, { once: true });
  }, [displayMode, leftDockWidth, rightDockWidth]);

  const zoomToContents = useCallback((target: StudioContentsTarget): void => {
    if (target.kind === 'component') {
      select(target.id);
      requestAnimationFrame(() => canvasRef.current?.fit('selection'));
      return;
    }
    if (target.kind === 'parcel') {
      if (target.id !== activeLayout.targetParcelId && !mutationBlocked) {
        onSelectParcel(target.id);
      }
      requestAnimationFrame(() => canvasRef.current?.fit('target-parcel'));
      return;
    }
    if (target.kind === 'exclusion' || target.kind === 'barrier' || target.kind === 'frontline') {
      canvasRef.current?.fit('issues');
      return;
    }
    if (target.kind === 'site-layer') {
      if (projection !== 'map') setProjection('map');
      requestAnimationFrame(() => canvasRef.current?.fit('layout'));
      return;
    }
    canvasRef.current?.fit('layout');
  }, [
    activeLayout.targetParcelId,
    mutationBlocked,
    onSelectParcel,
    projection,
    select,
    setProjection,
  ]);

  const navigateVerifyIssue = useCallback((issue: StudioVerifyIssueNavigation): void => {
    const firstComponentId = issue.componentIds[0];
    if (firstComponentId) {
      select(firstComponentId);
      setIssueTraceId(firstComponentId);
      if (issueTraceTimerRef.current) clearTimeout(issueTraceTimerRef.current);
      issueTraceTimerRef.current = setTimeout(() => {
        setIssueTraceId(current => current === firstComponentId ? null : current);
      }, 1_650);
    }
    const fitIssue = (): void => {
      if (issue.fitTarget === 'target-parcel') {
        canvasRef.current?.fit('target-parcel');
      } else {
        canvasRef.current?.fit(issue.componentIds.length === 1 ? 'selection' : 'issues');
      }
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(fitIssue);
    else fitIssue();
  }, [select]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      if (event.key === 'Escape') {
        if (importOpen || exportBundle) return;
        if (pendingBarrier) {
          event.preventDefault();
          setPendingBarrier(null);
          setPendingHeight('');
          setPendingLabel('');
          return;
        }
        if (pendingRoad) {
          event.preventDefault();
          setPendingRoad(null);
          setPendingRoadWidth('');
          setPendingRoadLabel('');
          return;
        }
        if (compactStudioSheets && (leftDockOpen || inspectorOpen)) {
          event.preventDefault();
          if (inspectorOpen) toggleInspector();
          else toggleLeftDock();
          return;
        }
        if (tool !== 'select') {
          event.preventDefault();
          cancelActiveTool();
        }
        return;
      }
      if (
        target instanceof HTMLElement
        && target.matches('input, textarea, select, [contenteditable="true"]')
      ) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redoLayout();
        else undoLayout();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        canvasRef.current?.zoomIn();
      } else if (event.key === '-') {
        event.preventDefault();
        canvasRef.current?.zoomOut();
      } else if (event.key.toLowerCase() === 'f') {
        event.preventDefault();
        canvasRef.current?.fit(event.shiftKey ? 'all-parcels' : 'layout');
      } else if (event.key.toLowerCase() === 'm') {
        setProjection('map');
      } else if (event.key.toLowerCase() === 'b') {
        setProjection('blueprint');
      } else if (event.key.toLowerCase() === 'v') {
        setTool('select');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    cancelActiveTool,
    compactStudioSheets,
    exportBundle,
    importOpen,
    inspectorOpen,
    leftDockOpen,
    pendingBarrier,
    pendingRoad,
    redoLayout,
    setProjection,
    setTool,
    tool,
    toggleInspector,
    toggleLeftDock,
    undoLayout,
  ]);

  const savePendingBarrier = useCallback(() => {
    if (!pendingBarrier) return;
    const heightM = Number(pendingHeight);
    if (!Number.isFinite(heightM) || heightM < BARRIER_MIN_HEIGHT_M || heightM > BARRIER_MAX_HEIGHT_M) {
      toast.error(`Barrier height must be ${BARRIER_MIN_HEIGHT_M}–${BARRIER_MAX_HEIGHT_M} m.`);
      return;
    }
    const label = pendingLabel.trim() !== ''
      ? pendingLabel.trim()
      : `${pendingBarrier.kind === 'berm' ? 'Berm' : pendingBarrier.kind === 'building' ? 'Building' : 'Wall'} ${acousticBarriers.length + 1}`;
    const barrier: GenerationAcousticBarrier = {
      id: `barrier-${crypto.randomUUID()}`,
      label,
      kind: pendingBarrier.kind,
      path: pendingBarrier.path,
      heightM,
      createdAt: new Date().toISOString(),
    };
    if (!commitLayoutChange('Add acoustic barrier', {
      acousticBarriers: [...acousticBarriers, barrier],
    })) return;
    setPendingBarrier(null);
    setPendingHeight('');
    setPendingLabel('');
    toast.success(`${label} added (${heightM} m).`);
  }, [pendingBarrier, pendingHeight, pendingLabel, acousticBarriers, commitLayoutChange]);

  const removeBarrier = useCallback((id: string) => {
    commitLayoutChange('Delete acoustic barrier', {
      acousticBarriers: acousticBarriers.filter((barrier) => barrier.id !== id),
    });
  }, [acousticBarriers, commitLayoutChange]);

  const fetchWind = useCallback(async () => {
    if (mutationBlocked) return;
    windAbortRef.current?.abort();
    const controller = new AbortController();
    windAbortRef.current = controller;
    const requestId = ++windRequestRef.current;
    const requestScope = windScope;
    setFetchingWind(true);
    try {
      const result = await fetchWindRose(anchor, { signal: controller.signal });
      if (
        controller.signal.aborted
        || windRequestRef.current !== requestId
        || windScopeRef.current !== requestScope
      ) return;
      if (result.status === 'available') {
        commitLayoutChange('Update wind rose', { windRose: result.windRose });
        toast.success(`Prevailing wind from ${result.windRose.prevailingFromDeg}° recorded.`);
      } else {
        toast.warning(`Wind rose unavailable: ${result.reason}`);
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        toast.error(error instanceof Error ? error.message : 'Wind rose request failed.');
      }
    } finally {
      if (
        windRequestRef.current === requestId
        && windScopeRef.current === requestScope
      ) {
        windAbortRef.current = null;
        setFetchingWind(false);
      }
    }
  }, [anchor, commitLayoutChange, mutationBlocked, windScope]);

  const alignCrosswind = useCallback(() => {
    if (!activeLayout.windRose) return;
    const rotation = crosswindRowRotationDeg(activeLayout.windRose.prevailingFromDeg);
    if (rotation != null) commitLayoutChange('Align rows crosswind', { rotationDeg: rotation });
  }, [activeLayout.windRose, commitLayoutChange]);

  const drawMode: 'line' | 'polygon' | null = tool === 'frontline'
    ? 'line'
    : tool === 'barrier-building' || tool === 'draw-custom-polygon'
      ? 'polygon'
      : tool === 'barrier-wall' || tool === 'barrier-berm' || tool === 'draw-road'
        ? 'line'
        : null;

  const onDrawComplete = useCallback((coordinates: Array<{ lat: number; lng: number }>) => {
    if (mutationBlockedRef.current) return;
    if (tool === 'frontline') {
      setTool('select');
      const start = coordinates[0];
      const end = coordinates.at(-1);
      if (!start || !end) return;
      const rotation = frontlineRotationDeg(start, end);
      if (rotation == null) return;
      commitLayoutChange('Set frontline alignment', {
        frontline: { start, end },
        rotationDeg: rotation,
      });
      return;
    }
    if (tool === 'barrier-wall' || tool === 'barrier-berm' || tool === 'barrier-building') {
      setPendingBarrier({
        kind: tool === 'barrier-wall' ? 'wall' : tool === 'barrier-berm' ? 'berm' : 'building',
        path: coordinates,
      });
      setTool('select');
      return;
    }
    if (tool === 'draw-road') {
      if (coordinates.length < 2) { setTool('select'); return; }
      setPendingRoad({ path: coordinates });
      setTool('select');
      return;
    }
    if (tool === 'draw-custom-polygon') {
      setTool('select');
      if (coordinates.length < 3) return;
      const id = `shape-${crypto.randomUUID().slice(0, 8)}`;
      commitLayoutChange('Draw custom footprint', addCustomComponent(activeLayout, {
        id,
        kind: 'custom',
        label: `Custom shape ${customCount + 1}`,
        shape: { type: 'polygon', path: coordinates },
        lifecycle: 'permanent',
        source: 'drawn',
      }));
      select(`custom-${id}`);
    }
  }, [tool, setTool, commitLayoutChange, activeLayout, customCount, select]);

  const savePendingRoad = useCallback(() => {
    if (!pendingRoad) return;
    const widthM = Number(pendingRoadWidth);
    if (!Number.isFinite(widthM) || widthM <= 0 || widthM > 100) {
      toast.error('Road corridor width must be between 0 and 100 m.');
      return;
    }
    const id = `road-${crypto.randomUUID().slice(0, 8)}`;
    const label = pendingRoadLabel.trim() !== '' ? pendingRoadLabel.trim() : `Road ${customCount + 1}`;
    if (!commitLayoutChange('Add road corridor', addCustomComponent(activeLayout, {
      id,
      kind: 'road',
      label,
      shape: { type: 'polyline', path: pendingRoad.path, widthM },
      lifecycle: 'permanent',
      source: 'drawn',
    }))) return;
    setPendingRoad(null);
    setPendingRoadWidth('');
    setPendingRoadLabel('');
    select(`custom-${id}`);
    toast.success(`${label} added (${widthM} m corridor).`);
  }, [pendingRoad, pendingRoadWidth, pendingRoadLabel, activeLayout, customCount, commitLayoutChange, select]);

  // Click-to-place: drop a default-size rect custom aligned with the plant
  // grid, then hand it straight to the inspector for numeric editing.
  const placeDefaults = PLACE_DEFAULTS[tool];
  const onPlaceClick = useCallback((point: { lat: number; lng: number }) => {
    if (!placeDefaults) return;
    const id = `${placeDefaults.kind}-${crypto.randomUUID().slice(0, 8)}`;
    commitLayoutChange(`Place ${placeDefaults.label}`, addCustomComponent(activeLayout, {
      id,
      kind: placeDefaults.kind,
      label: `${placeDefaults.label} ${customCount + 1}`,
      shape: { type: 'rect', widthM: placeDefaults.widthM, heightM: placeDefaults.heightM },
      position: point,
      rotationDeg: activeLayout.rotationDeg,
      lifecycle: 'permanent',
      source: 'drawn',
    }));
    setTool('select');
    select(`custom-${id}`);
  }, [placeDefaults, activeLayout, customCount, commitLayoutChange, setTool, select]);

  // Placed equipment (the fleet + shared singletons) with world centroids —
  // the targets Assist › Connect with roads draws access centerlines to.
  const equipmentComponentTargets = useMemo(() => {
    const kinds = new Set(['generation-block', 'cooling', 'switchyard', 'ccs-capture', 'ccs-compression']);
    return testFit.components
      .filter(component => kinds.has(component.rectangle.kind) && component.coordinates.length > 0)
      .map(component => ({
        id: component.rectangle.id,
        center: {
          lat: component.coordinates.reduce((sum, point) => sum + point.lat, 0) / component.coordinates.length,
          lng: component.coordinates.reduce((sum, point) => sum + point.lng, 0) / component.coordinates.length,
        },
      }));
  }, [testFit.components]);

  // Assist › Connect with roads: the clicked point is the entrance; planning-
  // grade access roads are drawn from it to every placed equipment component.
  const handleConnectRoadsClick = useCallback((entrance: { lat: number; lng: number }) => {
    const patch = connectWithRoadsPatch(activeLayout, entrance, equipmentComponentTargets, CONNECTOR_ROAD_WIDTH_M);
    if (patch && commitLayoutChange('Connect with roads', patch)) {
      toast.success(`Connected ${equipmentComponentTargets.length} component${equipmentComponentTargets.length === 1 ? '' : 's'} with roads`);
    } else {
      toast.warning('Place equipment before connecting roads.');
    }
    setTool('select');
  }, [activeLayout, equipmentComponentTargets, commitLayoutChange, setTool]);

  // Route a map click to the armed tool: connect-roads sets the entrance,
  // otherwise a place tool drops a default component.
  const onMapClick = useCallback((point: { lat: number; lng: number }) => {
    if (tool === 'connect-roads') handleConnectRoadsClick(point);
    else onPlaceClick(point);
  }, [tool, handleConnectRoadsClick, onPlaceClick]);

  // Assist › Pack Selection: grid-arrange the current multi-selection using the
  // scenario's component gap as the cell spacing.
  // Bulk placement — the two ends of the auto ⇄ manual axis the Placement
  // readout reports. Both route through `commitLayoutChange`, so undo covers
  // them (unpinning discards hand-placement intent, so that matters).
  const handleUnpinAll = useCallback(() => {
    const patch = unpinAllComponents(activeLayout);
    if (!patch) return;
    const released = activeLayout.manualPlacements?.length ?? 0;
    if (commitLayoutChange('Unpin all components', patch)) {
      toast.success(`Unpinned ${released} component${released === 1 ? '' : 's'} — the packer arranges the layout`);
    }
  }, [activeLayout, commitLayoutChange]);

  const handlePinAll = useCallback(() => {
    const patch = pinAllComponents(
      activeLayout,
      testFit.components.map(component => component.rectangle),
    );
    if (!patch) return;
    const pinned = patch.manualPlacements?.length ?? 0;
    if (commitLayoutChange('Pin all components', patch)) {
      toast.success(`Pinned ${pinned} component${pinned === 1 ? '' : 's'} in place`);
    }
  }, [activeLayout, testFit.components, commitLayoutChange]);

  const handlePackSelection = useCallback(() => {
    if (selectionLocked) return;
    const patch = packSelectionPatch(activeLayout, selectedComponents, anchor, {
      spacingM: activeLayout.componentGapM > 0 ? activeLayout.componentGapM : 20,
    });
    if (patch && commitLayoutChange('Pack selection', patch)) {
      toast.success(`Packed ${selectedComponents.length} components`);
    }
  }, [activeLayout, selectedComponents, anchor, commitLayoutChange, selectionLocked]);

  // Select-then-drag: the selected component moves as a rigid shape; a drag
  // on a packed component auto-pins it at the drop position (schemaVersion 2).
  const canDrag = useCallback((componentId: string) => {
    if (
      mutationBlocked
      || tool !== 'select'
      || selectedId !== componentId
      || lockedObjectIdSet.has(componentId)
    ) return false;
    const component = testFit.components
      .find(candidate => candidate.rectangle.id === componentId);
    return component != null && component.rectangle.kind !== 'access-road';
  }, [lockedObjectIdSet, mutationBlocked, selectedId, testFit.components, tool]);

  const onComponentDragEnd = useCallback((
    componentId: string,
    draggedCentroid: { lat: number; lng: number },
  ) => {
    if (!canDrag(componentId)) return;
    const component = testFit.components
      .find(candidate => candidate.rectangle.id === componentId);
    if (!component) return;
    const patch = componentDragPatch(
      activeLayout,
      component,
      anchor,
      draggedCentroid,
      new Date().toISOString(),
    );
    if (patch) commitLayoutChange('Move component', patch);
  }, [testFit.components, anchor, activeLayout, canDrag, commitLayoutChange]);

  const requestExport = useCallback((format: StudioExportFormat): void => {
    setExportFormat(format);
    setExportBundle(bundleFromTestFit(
      testFit,
      `test-fit-${scenarioKey}`,
      {
        siteId: site.id,
        scenarioId: scenarioId ?? 'draft',
        revision: scenarioRevision ?? 'Unsaved draft',
        referenceLayers: studioSiteLayers,
      },
    ));
  }, [scenarioId, scenarioKey, scenarioRevision, site.id, studioSiteLayers, testFit]);

  const confirmExport = useCallback(async () => {
    if (!exportBundle || !exportFormat) return;
    setExporting(true);
    try {
      const filename = exportFormat === 'kmz'
        ? await downloadBundleAsKmz(exportBundle)
        : exportFormat === 'shapefile'
          ? await downloadBundleAsShapefile(exportBundle)
          : exportFormat === 'dxf'
            ? downloadBundleAsDxf(exportBundle)
            : await downloadBundleAsDwg(exportBundle);
      toast.success(`Exported ${filename}.`);
      setExportBundle(null);
      setExportFormat(null);
    } catch (error) {
      if (error instanceof DwgExportUnavailableError) {
        toast.warning(error.message);
        return;
      }
      toast.error(error instanceof Error ? error.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }, [exportBundle, exportFormat]);

  const componentBadge = useMemo(() => {
    const excludedAcres = testFit.usable?.excludedAcres;
    const parts = [
      testFit.status === 'fits'
        ? 'Contained'
        : testFit.status === 'partial' ? 'Fit issue' : 'Unverified',
      testFit.containmentPct != null ? `${testFit.containmentPct.toFixed(1)}% contained` : null,
      excludedAcres != null ? `${excludedAcres.toFixed(1)} ac excluded` : null,
      testFit.exclusionOverlapAcres != null && testFit.exclusionOverlapAcres > 0.01
        ? `${testFit.exclusionOverlapAcres.toFixed(1)} ac on exclusions`
        : null,
    ];
    return parts.filter((part): part is string => part != null).join(' · ');
  }, [testFit]);

  return (
    <div
      className="grid h-full min-h-0 min-w-0 w-full grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden bg-background"
      data-testid="test-fit-studio"
      data-display-mode={displayMode}
      data-phone-review={phoneReviewMode ? 'true' : 'false'}
    >
      {displayMode === 'preview' ? (
        <div className="flex min-h-12 min-w-0 items-center gap-3 border-b border-border bg-card px-3">
          <div
            className="inline-flex shrink-0 overflow-hidden border border-border bg-background shadow-sm rounded-md"
            role="group"
            aria-label="Preview projection"
            data-testid="studio-preview-projection"
          >
            {(['map', 'blueprint'] as const).map((nextProjection, index) => (
              <button
                key={nextProjection}
                type="button"
                aria-pressed={projection === nextProjection}
                onClick={() => setProjection(nextProjection)}
                className={`min-h-9 min-w-[4.25rem] px-3 text-[10px] font-bold uppercase tracking-wider transition-colors focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary max-[599px]:min-h-11 ${
                  index > 0 ? 'border-l border-border' : ''
                } ${
                  projection === nextProjection
                    ? 'bg-primary/10 text-primary'
                    : 'text-text-secondary hover:bg-card hover:text-text-primary'
                }`}
                data-testid={`studio-preview-projection-${nextProjection}`}
              >
                {nextProjection === 'map' ? 'Map' : 'Blueprint'}
              </button>
            ))}
          </div>
          <span className="hidden min-w-0 truncate text-[10px] text-text-muted sm:inline">
            Review the current fit here. Open Studio for precision editing.
          </span>
          {onOpenStudio && (
            <button
              type="button"
              onClick={onOpenStudio}
              className="ml-auto min-h-9 shrink-0 border border-primary bg-primary/10 px-3 text-[10px] font-bold uppercase tracking-wider text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary max-[599px]:min-h-11 rounded-md"
              data-testid="studio-preview-open"
            >
              Open Studio
            </button>
          )}
        </div>
      ) : (
        <div className="flex min-w-0 items-stretch">
          <div className="min-w-0 flex-1">
            <StudioRibbon
              legacy={displayMode === 'legacy'}
              tool={tool}
              onSetTool={setTool}
              projection={projection}
              onSetProjection={setProjection}
              ribbonTab={ribbonTab}
              onSetRibbonTab={setRibbonTab}
              leftPane={leftPane}
              leftDockOpen={leftDockOpen}
              onSetLeftPane={setLeftPane}
              onToggleLeftDock={toggleLeftDockForViewport}
              contentsOpen={contentsOpen}
              onToggleContents={toggleContents}
              layoutPanelOpen={layoutPanelOpen}
              onToggleLayoutPanel={toggleLayoutPanel}
              inspectorOpen={inspectorOpen}
              onToggleInspector={toggleInspectorForViewport}
              onUndo={undoLayout}
              onRedo={redoLayout}
              canUndo={layoutHistory.past.length > 0}
              canRedo={layoutHistory.future.length > 0}
              onCreateStartingLayout={handleCreateStartingLayout}
              hasManualPlacements={(activeLayout.manualPlacements?.length ?? 0) > 0}
              onPackSelection={handlePackSelection}
              selectionCount={selectedIds.length}
              pinnedCount={placementCounts.pinned}
              autoCount={placementCounts.auto}
              onUnpinAll={handleUnpinAll}
              onPinAll={handlePinAll}
              readOnly={mutationBlocked}
              noiseAvailable={noiseField != null}
              noiseVisible={noiseVisible}
              onToggleNoise={onToggleNoise}
              frontlineSet={activeLayout.frontline != null}
              onClearFrontline={() => {
                commitLayoutChange('Clear frontline alignment', { frontline: undefined });
              }}
              windRose={activeLayout.windRose}
              fetchingWind={fetchingWind}
              onFetchWind={() => void fetchWind()}
              onAlignCrosswind={alignCrosswind}
              onOpenImport={() => { setImportFiles(null); setImportOpen(true); }}
              exporting={exporting}
              onExport={requestExport}
              layoutControls={(
                <StudioLayoutBar
                  testFit={testFit}
                  parcels={parcels}
                  activeLayout={activeLayout}
                  readOnly={mutationBlocked}
                  onPatch={(patch) => { commitLayoutChange('Update layout settings', patch); }}
                  onSelectParcel={(parcelId) => { if (!mutationBlocked) onSelectParcel(parcelId); }}
                  sessionKey={scenarioKey}
                  siteLayers={siteLayers}
                  layoutSearchBase={layoutSearchBase}
                  pinnedCount={placementCounts.pinned}
                  autoCount={placementCounts.auto}
                  onCreateStartingLayout={handleCreateStartingLayout}
                  hasManualPlacements={(activeLayout.manualPlacements?.length ?? 0) > 0}
                  onPackSelection={handlePackSelection}
                  selectionCount={selectedIds.length}
                  onConnectRoads={() => setTool('connect-roads')}
                  onAlignCrosswind={alignCrosswind}
                  windAvailable={activeLayout.windRose != null}
                />
              )}
            />
          </div>
        </div>
      )}
      <div className="relative flex min-h-0 min-w-0 overflow-hidden">
        {compactStudioSheets && (leftDockOpen || inspectorOpen) && (
          <button
            type="button"
            className="absolute inset-0 z-20 bg-black/35"
            aria-label="Close Studio side sheet"
            onClick={() => {
              if (inspectorOpen) toggleInspector();
              else toggleLeftDock();
            }}
            data-testid="studio-sheet-backdrop"
          />
        )}
        {displayMode !== 'preview' && leftDockOpen && (
          <aside
            className="absolute inset-y-0 left-0 z-30 flex max-w-[calc(100%-2rem)] shrink-0 flex-col overflow-hidden border-r border-border bg-card shadow-2xl min-[960px]:relative min-[960px]:inset-auto min-[960px]:z-auto min-[960px]:shadow-none"
            style={{ width: leftDockWidth }}
            aria-label="Studio layout and layers"
            data-testid="studio-left-dock"
            {...(compactStudioSheets ? { role: 'dialog', 'aria-modal': true } : {})}
          >
            <div
              className="flex shrink-0 items-center border-b border-border px-3 py-2.5"
              data-testid="studio-left-dock-header"
            >
              <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-text-secondary">Layers</span>
            </div>
            <div
              id="studio-left-panel-layers"
              role="region"
              aria-label="Layers"
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
            >
              {(
                <StudioContentsPane
                  testFit={testFit}
                  selectedId={selectedId}
                  selectedIds={selectedIds}
                  onSelect={handleSelect}
                  importedAssignments={activeLayout.importedAssignments}
                  onUnassignSource={mutationBlocked ? undefined : handleUnassignSource}
                  parcels={parcels}
                  siteLayers={studioSiteLayers}
                  acousticBarriers={acousticBarriers}
                  frontlineSet={activeLayout.frontline != null}
                  noiseAvailable={noiseField != null}
                  noiseVisible={noiseVisible}
                  onToggleNoise={onToggleNoise}
                  onToggleSiteLayer={(layerId, visible) => {
                    setLayerVisibility(current => ({ ...current, [layerId]: visible }));
                  }}
                  hiddenCategories={hiddenCategories}
                  onToggleCategory={toggleCategory}
                  siteLayerOpacity={siteLayerOpacity}
                  onSetSiteLayerOpacity={(layerId, opacity) => {
                    setSiteLayerOpacity(current => ({ ...current, [layerId]: opacity }));
                  }}
                  hiddenComponentIds={hiddenComponentIdSet}
                  lockedObjectIds={lockedObjectIdSet}
                  onToggleComponentVisibility={(componentId) => {
                    setHiddenComponentIds(current => ({
                      ...current,
                      [componentId]: !current[componentId],
                    }));
                    if (selectedId === componentId) select(null);
                  }}
                  onToggleObjectLock={(objectId) => {
                    setLockedObjectIds(current => ({
                      ...current,
                      [objectId]: !current[objectId],
                    }));
                  }}
                  onZoomTo={zoomToContents}
                  onHideSiteLayers={hideSiteLayers}
                  onCopySiteLayers={mutationBlocked || selectionLocked ? undefined : copySiteLayers}
                  onDeleteSiteLayers={mutationBlocked || selectionLocked ? undefined : deleteSiteLayers}
                  onMergeSiteLayers={mutationBlocked || selectionLocked ? undefined : mergeSiteLayersAction}
                  onMoveSiteLayers={mutationBlocked || selectionLocked ? undefined : moveSiteLayers}
                />
              )}
            </div>
            <button
              type="button"
              aria-label="Resize left dock"
              title="Drag to resize the Layout and Layers dock"
              onPointerDown={(event) => beginDockResize('left', event)}
              className="absolute inset-y-0 right-0 z-20 hidden w-3 cursor-col-resize touch-none xl:block"
            />
          </aside>
        )}
        <div
          className="relative min-h-0 min-w-0 flex-1 overflow-hidden"
          data-testid="studio-canvas-host"
          onDragOver={(event) => {
            if (mutationBlocked || !event.dataTransfer.types.includes('Files')) return;
            event.preventDefault();
          }}
          onDrop={(event) => {
            if (mutationBlocked) return;
            const files = Array.from(event.dataTransfer.files ?? []);
            if (files.length === 0) return;
            event.preventDefault();
            setImportFiles(files);
            setImportOpen(true);
          }}
        >
          <StudioCanvas
            provisionalBasis={equipmentComponentTargets.length === 0}
            ref={canvasRef}
            projection={projection}
            {...(displayMode === 'editor' ? { onProjectionChange: setProjection } : {})}
            showCameraControls={displayMode !== 'legacy'}
            testFit={testFit}
            parcels={parcels}
            frontline={activeLayout.frontline}
            noiseField={noiseField}
            noiseVisible={noiseVisible}
            acousticBarriers={acousticBarriers}
            selectedId={selectedId}
            selectedIds={selectedIds}
            selectionMode={tool === 'select'}
            traceId={issueTraceId}
            onSelect={handleSelect}
            drawMode={drawMode}
            drawColor={tool === 'frontline'
              ? FRONTLINE_COLOR
              : tool === 'draw-road'
                ? ROAD_DRAW_COLOR
                : tool === 'draw-custom-polygon'
                  ? CUSTOM_DRAW_COLOR
                  : BARRIER_COLOR}
            onDrawComplete={onDrawComplete}
            onDrawCancel={() => setTool('select')}
            {...((placeDefaults || tool === 'connect-roads') && !mutationBlocked
              ? { onPlaceClick: onMapClick }
              : {})}
            canDrag={canDrag}
            onComponentDragEnd={onComponentDragEnd}
            siteLayers={studioSiteLayers}
            siteLayerOpacity={siteLayerOpacity}
            hiddenComponentIds={hiddenComponentIdSet}
            lockedObjectIds={lockedObjectIdSet}
            viewportKey={scenarioKey}
            gestureMode={displayMode === 'preview' ? 'cooperative' : 'greedy'}
          />
          {/* Bottom-LEFT: fit/issue status only. The wind rose used to stack
              here and collided with the sheet's scale bar and parcel dims. */}
          <div className="pointer-events-none absolute bottom-2 left-2 flex flex-col items-start gap-1.5">
            <span className="pointer-events-auto border border-border bg-card/80 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.14em] text-text-secondary shadow-sm backdrop-blur-sm rounded-md">
              {componentBadge}
            </span>
          </div>

          {/* Bottom-RIGHT: the wind rose, in the one free corner (camera is
              top-left, north top-right, status bottom-left). Sits above the
              sheet's own bottom rule so it never covers the fit readout. */}
          {activeLayout.windRose && windVisible && (
            <div
              className="pointer-events-none absolute bottom-8 right-3 flex flex-col items-end gap-1"
              data-testid="studio-wind-overlay"
            >
              <WindRoseBadge windRose={activeLayout.windRose} />
              <span className="rounded-md border border-border bg-card/90 px-2 py-1 text-[9px] uppercase tracking-wider text-text-secondary">
                Wind from {activeLayout.windRose.prevailingFromDeg}° · calm {activeLayout.windRose.calmPct.toFixed(0)}%
              </span>
            </div>
          )}

          {/* Required-height form for a freshly sketched screen. */}
          {pendingBarrier && (
            <div
              className="absolute inset-x-0 top-12 z-20 mx-auto w-64 border border-cyan-500 bg-card p-3 shadow-lg rounded-lg"
              data-testid="generation-barrier-height-form"
            >
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-text-primary">
                {pendingBarrier.kind === 'berm' ? 'Berm' : pendingBarrier.kind === 'building' ? 'Building' : 'Wall'} height (required)
              </p>
              <label
                htmlFor="generation-barrier-label-input"
                className="mb-1 block text-[9px] font-semibold uppercase tracking-wider text-text-muted"
              >
                Label <span className="normal-case tracking-normal">(optional)</span>
              </label>
              <input
                id="generation-barrier-label-input"
                type="text"
                value={pendingLabel}
                onChange={(event) => setPendingLabel(event.target.value)}
                className="mb-2 min-h-10 w-full border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                data-testid="generation-barrier-label-input"
              />
              <label
                htmlFor="generation-barrier-height-input"
                className="mb-1 block text-[9px] font-semibold uppercase tracking-wider text-text-muted"
              >
                Height
              </label>
              <div className="mb-2 flex items-center gap-2">
                <input
                  id="generation-barrier-height-input"
                  type="number"
                  min={BARRIER_MIN_HEIGHT_M}
                  max={BARRIER_MAX_HEIGHT_M}
                  step={0.5}
                  value={pendingHeight}
                  onChange={(event) => setPendingHeight(event.target.value)}
                  placeholder={`${BARRIER_MIN_HEIGHT_M}–${BARRIER_MAX_HEIGHT_M}`}
                  aria-describedby="generation-barrier-height-help"
                  className="min-h-10 w-full border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                  data-testid="generation-barrier-height-input"
                />
                <span id="generation-barrier-height-help" className="text-[10px] text-text-muted">
                  metres · {BARRIER_MIN_HEIGHT_M}–{BARRIER_MAX_HEIGHT_M}
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setPendingBarrier(null); setPendingHeight(''); setPendingLabel(''); }}
                  className="min-h-11 border border-border px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary rounded-md"
                  data-testid="generation-barrier-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={savePendingBarrier}
                  className="min-h-11 border border-cyan-500 bg-cyan-500/10 px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-cyan-500 rounded-md"
                  data-testid="generation-barrier-save"
                >
                  Add barrier
                </button>
              </div>
            </div>
          )}

          {/* Required-width form for a freshly sketched road centerline. */}
          {pendingRoad && (
            <div
              className="absolute inset-x-0 top-12 z-20 mx-auto w-64 border border-border bg-card p-3 shadow-lg rounded-lg"
              data-testid="studio-road-width-form"
            >
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-text-primary">
                Road corridor width (required)
              </p>
              <label
                htmlFor="studio-road-label-input"
                className="mb-1 block text-[9px] font-semibold uppercase tracking-wider text-text-muted"
              >
                Label <span className="normal-case tracking-normal">(optional)</span>
              </label>
              <input
                id="studio-road-label-input"
                type="text"
                value={pendingRoadLabel}
                onChange={(event) => setPendingRoadLabel(event.target.value)}
                className="mb-2 min-h-10 w-full border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                data-testid="studio-road-label-input"
              />
              <label
                htmlFor="studio-road-width-input"
                className="mb-1 block text-[9px] font-semibold uppercase tracking-wider text-text-muted"
              >
                Corridor width
              </label>
              <div className="mb-2 flex items-center gap-2">
                <input
                  id="studio-road-width-input"
                  type="number"
                  min={1}
                  max={100}
                  step={0.5}
                  value={pendingRoadWidth}
                  onChange={(event) => setPendingRoadWidth(event.target.value)}
                  placeholder="e.g. 12"
                  aria-describedby="studio-road-width-help"
                  className="min-h-10 w-full border border-border bg-background px-2 py-1 text-xs text-text-primary rounded-md"
                  data-testid="studio-road-width-input"
                />
                <span id="studio-road-width-help" className="text-[10px] text-text-muted">
                  metres · 1–100
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setPendingRoad(null); setPendingRoadWidth(''); setPendingRoadLabel(''); }}
                  className="min-h-11 border border-border px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary rounded-md"
                  data-testid="studio-road-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={savePendingRoad}
                  className="min-h-11 border border-primary bg-primary/10 px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-primary rounded-md"
                  data-testid="studio-road-save"
                >
                  Add road
                </button>
              </div>
            </div>
          )}

          {/* Screen inventory: compact list with heights + delete. */}
          {acousticBarriers.length > 0 && projection === 'map' && (
            <div
              className="pointer-events-auto absolute bottom-2 right-2 max-h-40 w-52 overflow-y-auto border border-border bg-card/95 p-2 rounded-md"
              data-testid="generation-barrier-list"
            >
              <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-text-muted">
                Acoustic barriers
              </p>
              {acousticBarriers.map((barrier) => (
                <div
                  key={barrier.id}
                  className="flex items-center gap-2 py-0.5"
                  data-testid={`generation-barrier-row-${barrier.id}`}
                >
                  <span className="h-2 w-2 shrink-0" style={{ backgroundColor: BARRIER_COLOR }} aria-hidden />
                  <span className="flex-1 truncate text-[10px] text-text-primary" title={barrier.label}>
                    {barrier.label}
                  </span>
                  <span className="shrink-0 text-[9px] text-text-muted">{barrier.heightM} m</span>
                  {!mutationBlocked && (
                    <button
                      type="button"
                      onClick={() => removeBarrier(barrier.id)}
                      className="shrink-0 text-text-muted hover:text-red-500"
                      aria-label={`Delete ${barrier.label}`}
                      data-testid={`generation-barrier-delete-${barrier.id}`}
                    >
                      <Trash2 className="h-3 w-3" aria-hidden />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {displayMode !== 'preview' && inspectorOpen && (
          <aside
            className="absolute inset-y-0 right-0 z-30 flex max-w-[calc(100%-2rem)] shrink-0 flex-col overflow-hidden border-l border-border bg-card shadow-2xl xl:relative xl:z-auto xl:shadow-none"
            style={{ width: rightDockWidth }}
            aria-label="Component inspector and fit verdict"
            data-testid="studio-inspector-dock"
            {...(compactStudioSheets ? { role: 'dialog', 'aria-modal': true } : {})}
          >
            {/* Right-dock tabs: Inspector (edit) | Verify (go/no-go). */}
            <div className="flex shrink-0 items-stretch gap-px border-b border-border" role="tablist" aria-label="Right dock">
              {([['inspector', 'Inspector'], ['verify', 'Verify']] as const).map(([pane, label]) => (
                <button
                  key={pane}
                  id={`studio-right-tab-${pane}`}
                  type="button"
                  role="tab"
                  aria-selected={rightPane === pane}
                  aria-controls={`studio-right-panel-${pane}`}
                  tabIndex={rightPane === pane ? 0 : -1}
                  onClick={() => setRightPane(pane)}
                  onKeyDown={(event) => {
                    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                    event.preventDefault();
                    const nextPane = pane === 'inspector' ? 'verify' : 'inspector';
                    setRightPane(nextPane);
                    requestAnimationFrame(() => {
                      document.getElementById(`studio-right-tab-${nextPane}`)?.focus();
                    });
                  }}
                  className={`min-h-10 flex-1 px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary max-[599px]:min-h-11 ${
                    rightPane === pane
                      ? 'border-b-2 border-primary bg-primary/5 text-primary'
                      : 'border-b-2 border-transparent text-text-muted hover:text-text-secondary'
                  }`}
                  data-testid={`studio-right-pane-${pane}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div
              id={`studio-right-panel-${rightPane}`}
              role="tabpanel"
              aria-labelledby={`studio-right-tab-${rightPane}`}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
            >
              {rightPane === 'verify' ? (
                <StudioVerifyRail
                  provisionalBasis={equipmentComponentTargets.length === 0}
                  testFit={testFit}
                  onNavigateIssue={navigateVerifyIssue}
                  maxPower={maxPower}
                  maxPowerBusy={maxPowerBusy}
                  onRunMaxPower={onRunMaxPower}
                  onApplyMaxPowerPlacement={applyMaxPowerPlacement}
                  applyPlacementBlockedReason={mutationBlocked
                    ? (readOnly
                      ? 'This scenario is read-only.'
                      : busy
                        ? 'Wait for the current save to finish.'
                        : 'Layout edits are unavailable in this view.')
                    : null}
                />
              ) : selectedIds.length >= 2 ? (
                <div>
                  {selectionLocked && (
                    <p className="px-3 pt-3 text-[10px] text-text-muted" role="status">
                      Unlock the selected objects to edit this selection.
                    </p>
                  )}
                  <StudioMultiSelectTools
                    components={selectedComponents}
                    activeLayout={activeLayout}
                    anchor={anchor}
                    readOnly={mutationBlocked || selectionLocked}
                    onCommit={commitLayoutChange}
                    onClearSelection={() => select(null)}
                  />
                </div>
              ) : selectedId != null ? (
                <StudioInspector
                  testFit={testFit}
                  activeLayout={activeLayout}
                  installedReservations={installedReservations}
                  readOnly={mutationBlocked || lockedObjectIdSet.has(selectedId)}
                  selectedId={selectedId}
                  siteLayers={studioSiteLayers}
                  siteLayerReadOnly={mutationBlocked || lockedObjectIdSet.has(selectedId)}
                  selectedLayerLocked={selectedId != null && lockedObjectIdSet.has(selectedId)}
                  parcels={parcels}
                  acousticBarriers={acousticBarriers}
                  onPatch={(patch) => {
                    commitLayoutChange('Edit component properties', patch);
                  }}
                  onSelect={select}
                  onPatchSiteLayer={patchSiteLayerField}
                  onDeleteSiteLayer={deleteSiteLayerById}
                />
              ) : (
                <div
                  className="flex h-full flex-col items-center justify-center gap-2 px-4 py-8 text-center"
                  data-testid="studio-inspector-empty"
                >
                  <MousePointer2 className="h-5 w-5 text-text-muted" aria-hidden />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-text-secondary">Inspector</p>
                  <p className="text-[10px] leading-relaxed text-text-muted">
                    Select a component on the canvas or in Contents to edit its dimensions, position and setbacks.
                  </p>
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label="Resize right dock"
              title="Drag to resize the Inspector and Verify dock"
              onPointerDown={(event) => beginDockResize('right', event)}
              className="absolute inset-y-0 left-0 z-20 hidden w-3 cursor-col-resize touch-none xl:block"
            />
          </aside>
        )}
      </div>
      <StudioStatusBar
        testFit={testFit}
        takeoffsOpen={takeoffsOpen}
        onToggleTakeoffs={toggleTakeoffs}
        fitOpen={fitOpen}
        onToggleFit={toggleFit}
        selectedCount={selectedIds.length}
        {...(tool === 'select' ? {} : { activeToolLabel: tool.replaceAll('-', ' ') })}
        announceFitStatus={displayMode === 'editor'}
        {...(displayMode === 'editor' && saveState ? { saveState } : {})}
      />
      <StudioImportDialog
        site={site}
        sessionKey={importSessionKey}
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportFiles(null); }}
        activeLayout={activeLayout}
        testFit={testFit}
        onPatchLayout={(patch) => (
          currentImportSessionRef.current === importSessionKey
          && !mutationBlockedRef.current
            ? commitLayoutChange('Import studio geometry', patch)
            : false
        )}
        {...(onAddGenerationBlock ? { onAddGenerationBlock } : {})}
        initialFiles={importFiles}
      />
      <StudioExportDialog
        open={exportBundle != null && exportFormat != null}
        onOpenChange={(open) => {
          if (open || exporting) return;
          setExportBundle(null);
          setExportFormat(null);
        }}
        bundle={exportBundle}
        format={exportFormat}
        exporting={exporting}
        onConfirm={() => void confirmExport()}
      />
    </div>
  );
};
